import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { LayoutDashboard, ClipboardList, Users, LifeBuoy, ArrowLeft, Bell, LogOut, Package } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ThemeToggle from "@/components/ThemeToggle";

const TeamLayout = () => {
    const { user, logout } = useAuth();
    const [notif, setNotif] = useState({ notifications: [], unread: 0 });

    useEffect(() => {
        const load = async () => {
            try { const { data } = await api.get("/notifications"); setNotif(data); } catch {}
        };
        load();
        const t = setInterval(load, 15000);
        return () => clearInterval(t);
    }, []);

    const isSales = user?.role === "sales" || user?.role === "admin" || user?.role === "super_admin";
    const isSupport = user?.role === "support" || user?.role === "admin" || user?.role === "super_admin";

    const items = [
        { to: "/team", end: true, label: "Overview", icon: LayoutDashboard, testId: "team-nav-overview" },
        { to: "/team/tasks", label: "Task Board", icon: ClipboardList, testId: "team-nav-tasks" },
        { to: "/team/orders", label: "Orders", icon: Package, testId: "team-nav-orders" },
        { to: "/team/products", label: "Products", icon: Package, testId: "team-nav-products" },
        { to: "/team/leads", label: "Leads", icon: Users, testId: "team-nav-leads" },
        ...(isSales ? [{ to: "/team/sales", label: "Sales", icon: Users, testId: "team-nav-sales" }] : []),
        ...(isSupport ? [{ to: "/team/support", label: "Support", icon: LifeBuoy, testId: "team-nav-support" }] : []),
    ];

    const markAll = async () => {
        try { await api.post("/notifications/read-all"); setNotif((n) => ({ ...n, unread: 0 })); } catch {}
    };

    return (
        <div className="min-h-screen flex" data-testid="team-layout">
            <aside className="w-60 shrink-0 bg-gradient-to-b from-primary to-primary/90 text-primary-foreground p-4 flex flex-col sticky top-0 h-screen">
                <Link to="/" className="flex items-center gap-2 mb-6">
                    <div className="w-9 h-9 rounded-full overflow-hidden ring-2 ring-brand-gold/60 bg-white">
                        <img src="https://customer-assets-cm19k8pv.emergentagent.net/job_custom-collectibles-2/artifacts/vm51spgg_Screenshot%202026-07-23%20at%206.35.36%E2%80%AFAM.png" alt="" className="w-full h-full object-cover" />
                    </div>
                    <div>
                        <div className="font-display text-lg font-bold leading-tight">Cherishables</div>
                        <div className="text-[10px] uppercase tracking-widest opacity-80">Team Portal</div>
                    </div>
                </Link>
                <nav className="space-y-1 flex-1">
                    {items.map((i) => (
                        <NavLink key={i.to} to={i.to} end={i.end} data-testid={i.testId}
                            className={({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-xl text-sm ${isActive ? "bg-white/15 font-semibold" : "hover:bg-white/10 text-white/85"}`}>
                            <i.icon className="w-4 h-4" /> {i.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="border-t border-white/15 pt-3 space-y-1">
                    <div className="px-3 py-1 text-xs opacity-70">Signed in as</div>
                    <div className="px-3 text-sm font-semibold truncate">{user?.name}</div>
                    <div className="px-3 text-[11px] opacity-70 uppercase tracking-widest">{user?.role}</div>
                    <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm hover:bg-white/10"><LogOut className="w-4 h-4" /> Log out</button>
                    <Link to="/" className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm hover:bg-white/10"><ArrowLeft className="w-4 h-4" /> Back to site</Link>
                </div>
            </aside>
            <main className="flex-1 min-w-0 relative">
                <div className="absolute top-4 right-6 z-10 flex items-center gap-2">
                    <button onClick={markAll} className="relative w-10 h-10 rounded-full glass flex items-center justify-center" data-testid="notif-btn">
                        <Bell className="w-4 h-4" />
                        {notif.unread > 0 && (
                            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-bold">{notif.unread}</span>
                        )}
                    </button>
                    <ThemeToggle />
                </div>
                <Outlet context={{ notif }} />
            </main>
        </div>
    );
};

export default TeamLayout;
